/* eslint-disable no-restricted-imports */
import { HTTPError } from 'ky';
import type { NormalizedOptions } from 'ky';

export type ApiErrorCode =
  | 'NETWORK_ERROR'
  | 'TIMEOUT'
  | 'UNAUTHORIZED'
  | 'FORBIDDEN'
  | 'NOT_FOUND'
  | 'VALIDATION_ERROR'
  | 'SERVER_ERROR'
  | 'UNKNOWN_ERROR';

export interface FieldError {
  field: string;
  message: string;
}

/**
 * Structured API error that extends Ky's HTTPError.
 * Produced by the error interceptor — consumers should catch this type.
 */
export class ApiError extends HTTPError {
  readonly code: ApiErrorCode;
  readonly statusCode: number | null;
  readonly fieldErrors: FieldError[] | undefined;
  /** Server-provided error code from the new contract envelope (`error.code`).
   *  Use this for precise server-side error discrimination (e.g. DUPLICATE_CONNECTION)
   *  where the frontend-derived `code` is too coarse. */
  readonly serverCode: string | undefined;
  /** Field-level validation errors from the contract envelope (`error.fields`).
   *  Keys are dotted paths (e.g. `credentials.merchant_id`); values are message arrays. */
  readonly fields: Record<string, string[]> | undefined;
  /** Normalised message list — populated when the server sends `message` as a string[].
   *  Contains a single entry when the server sends a plain string. */
  readonly messages: string[];
  /** Server-side support reference (`details.reference`) that appears verbatim in
   *  the server log — surface it in any "contact support" UI so a report can be
   *  tied to a log line. Undefined when the route doesn't send one. */
  readonly reference: string | undefined;
  /** The parsed JSON error body, unmodified — for routes whose envelope carries
   *  structured extras the fields above don't model (e.g. transformer `problems[]`). */
  readonly body: unknown;

  constructor(
    code: ApiErrorCode,
    statusCode: number | null,
    message: string,
    response: Response,
    request: Request,
    options: NormalizedOptions,
    fieldErrors?: FieldError[],
    serverCode?: string,
    fields?: Record<string, string[]>,
    messages?: string[],
    reference?: string,
    body?: unknown,
  ) {
    super(response, request, options);
    this.name = 'ApiError';
    this.message = message;
    this.code = code;
    this.statusCode = statusCode;
    this.fieldErrors = fieldErrors;
    this.serverCode = serverCode;
    this.fields = fields;
    this.messages = messages ?? (message ? [message] : []);
    this.reference = reference;
    this.body = body;
  }
}

/**
 * Thrown by service-layer fetchers whose backend endpoint does not exist
 * yet. Consumers see `isError` on the query and render their existing
 * error branch — no fake placeholder data ever reaches the UI. Distinct
 * from `ApiError` because no `Response` / `Request` pair is involved
 * (nothing went over the wire).
 */
export class NotImplementedError extends Error {
  readonly endpoint: string;

  constructor(endpoint: string) {
    super(`Endpoint not implemented: ${endpoint}`);
    this.name = 'NotImplementedError';
    this.endpoint = endpoint;
  }
}

/** Map an HTTP status code to a typed ApiErrorCode. */
export function statusToErrorCode(status: number): ApiErrorCode {
  switch (status) {
    case 401:
      return 'UNAUTHORIZED';
    case 403:
      return 'FORBIDDEN';
    case 404:
      return 'NOT_FOUND';
    case 422:
      return 'VALIDATION_ERROR';
    default:
      return status >= 500 ? 'SERVER_ERROR' : 'UNKNOWN_ERROR';
  }
}
